import { useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import DifferenceOutlinedIcon from '@mui/icons-material/DifferenceOutlined'
import { api } from '../../api/client.js'
import { streamOperation } from '../../api/operations.js'
import { errorMessage, errorCode } from '../../api/errors.js'
import { tokens } from '../../theme.js'
import {
  Dialog,
  WideDialog,
  DialogTitle,
  DialogActions,
  Button,
  SecondaryButton,
  MedBodyText,
  MutedText,
  NativeSelect,
  TableCheckbox,
  RadioButton,
  ErrorText,
  Spinner,
  plural
} from '../styles/index.js'
import { CompareDuplicates, DUPLICATE_NOTE } from './CompareDuplicates.js'
import { initialDecisions, decide as decideOn, keepAll, excludedIds } from './dedupReview.js'
import { folderPath } from '../files/FolderTree.js'
import { subtreeIds } from '../files/directory.js'
import { pdfLike } from '../files/fileStatus.js'
import { fileCodeCopy } from '../common/errorCopy.js'

const colors = tokens.colors

const Option = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  & > .MuiRadio-root {
    margin-top: 1px;
  }
`

const comparable = f => pdfLike(f) && f.kind !== 'COMBINED'

function candidatesIn(files, folders, folderId, recursive) {
  const pdfs = files.filter(comparable)
  if (!folderId) return pdfs
  const ids = recursive ? subtreeIds(folders, folderId) : new Set([folderId])
  return pdfs.filter(f => ids.has(f.folderId))
}

const refusals = e =>
  [...new Set((e.response?.data?.rejected ?? []).map(r => fileCodeCopy(r.code, 'cannot be compared')))].map(s => s.toLowerCase()).join(', ') ||
  'no PDF yet, excluded, or the file combine made'

export function DedupDialog({ open, projectId, files, folders, activeFolderId, selection = [], onClose, onApplied }) {
  const [phase, setPhase] = useState('scope') // scope | running | review | empty
  const [mode, setMode] = useState(selection.length ? 'selection' : activeFolderId ? 'folder' : 'project')
  const [folderId, setFolderId] = useState(activeFolderId || folders[0]?.id || '')
  const [recursive, setRecursive] = useState(true)
  const [error, setError] = useState('')
  const [compared, setCompared] = useState(0)
  const [groups, setGroups] = useState([])
  const [index, setIndex] = useState(0)
  const [decisions, setDecisions] = useState({}) // fileId -> 'keep' | 'exclude'
  const [visited, setVisited] = useState(new Set())
  const [note, setNote] = useState(DUPLICATE_NOTE)
  const [operationId, setOperationId] = useState(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (!open) return
    setPhase('scope')
    setError('')
    setGroups([])
    setIndex(0)
    setDecisions({})
    setVisited(new Set())
    setBusy(false)
    setNote(DUPLICATE_NOTE)
    setOperationId(null)
    setMode(selection.length ? 'selection' : activeFolderId ? 'folder' : 'project')
    setFolderId(activeFolderId || folders[0]?.id || '')
    setRecursive(true)
  }, [open])

  const byId = useMemo(() => new Map(files.map(f => [f.id, f])), [files])
  const chosen = mode === 'selection'
  const scopeFolderId = !chosen && mode === 'folder' ? folderId : null
  const unusable = selection.filter(f => !comparable(f)).length
  const candidates = chosen ? selection : candidatesIn(files, folders, scopeFolderId, recursive)
  const scopeLabel = chosen
    ? `the ${plural(selection.length, 'selected file')}`
    : scopeFolderId
      ? `“${folderPath(folders, scopeFolderId)}”${recursive ? ' and its subfolders' : ''}`
      : 'the whole project'

  async function start() {
    setError('')
    try {
      const scope = chosen ? { fileIds: selection.map(f => f.id) } : scopeFolderId ? { folderId: scopeFolderId, recursive } : {}
      const { data } = await api.post(`/api/projects/${projectId}/dedup`, { scope })
      setOperationId(data.operationId)
      setCompared(candidates.length)
      setPhase('running')
      const op = await streamOperation(data.operationId)
      if (op.status !== 'DONE') throw new Error(op.result?.error || 'the comparison failed')
      const found = op.result?.duplicateGroups ?? []
      setGroups(found)
      setDecisions(initialDecisions(found))
      setIndex(0)
      setVisited(new Set([0]))
      setPhase(found.length ? 'review' : 'empty')
    } catch (e) {
      setPhase('scope')
      const code = errorCode(e)
      setError(
        code === 'NOT_ENOUGH_FILES'
          ? 'Nothing to compare: this scope has fewer than two converted PDFs. Convert files first.'
          : code === 'FILES_REJECTED'
            ? `Some of the selected files cannot be compared: ${refusals(e)}. Narrow the selection, or fix those files first.`
            : errorMessage(e)
      )
    }
  }

  const group = groups[index]
  const excludeIds = excludedIds(groups, visited, decisions)
  const enough = candidates.length >= 2 && !(chosen && unusable)
  const goTo = i => {
    setIndex(i)
    setVisited(v => new Set([...v, i]))
  }
  const decide = (fileId, d) => setDecisions(m => decideOn(m, fileId, d))
  const skip = () => {
    const kept = keepAll(group, decisions)
    setDecisions(kept)
    if (index < groups.length - 1) goTo(index + 1)
    else finish(excludedIds(groups, visited, kept))
  }
  const forward = () => (index < groups.length - 1 ? goTo(index + 1) : finish())
  async function finish(ids = excludeIds) {
    if (busy) return
    if (!ids.length) return onClose()
    setBusy(true)
    try {
      const { data } = await api.post(`/api/projects/${projectId}/files/exclude`, { fileIds: ids, note, dedupOperationId: operationId })
      onApplied(data)
    } catch (e) {
      setError(errorMessage(e))
      setBusy(false)
    }
  }

  if (phase === 'review' && group) {
    return (
      <WideDialog open={open} onClose={busy ? undefined : onClose} data-testid="dedup-dialog">
        <CompareDuplicates
          group={group}
          groups={groups}
          index={index}
          files={files}
          folders={folders}
          decisions={decisions}
          note={note}
          excludeCount={excludeIds.length}
          busy={busy}
          error={error}
          onDecide={decide}
          onNote={setNote}
          onBack={() => goTo(index - 1)}
          onSkip={skip}
          onForward={forward}
          onClose={onClose}
        />
      </WideDialog>
    )
  }

  if (phase === 'empty') {
    return (
      <Dialog open={open} onClose={onClose} width={440} data-testid="dedup-dialog">
        <DialogTitle>No duplicates found</DialogTitle>
        <MedBodyText data-testid="dedup-empty">
          Compared {plural(compared, 'file')} in {scopeLabel}. Nothing to review.
        </MedBodyText>
        <DialogActions>
          <Button type="button" onClick={onClose} data-testid="dedup-close">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  const running = phase === 'running'
  return (
    <Dialog open={open} onClose={onClose} width={440} data-testid="dedup-dialog">
      <DialogTitle style={{ marginBottom: 4 }}>Find duplicates</DialogTitle>
      <MutedText style={{ marginBottom: 18 }}>Compares file content with stored embeddings; files already embedded are not re-embedded.</MutedText>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, opacity: running ? 0.6 : 1 }}>
        {selection.length > 0 && (
          <Option>
            <RadioButton
              checked={chosen}
              onChange={() => setMode('selection')}
              disabled={running}
              inputProps={{ 'data-testid': 'dedup-scope-selection' }}
            />
            <div>
              <MedBodyText>Selected files</MedBodyText>
              <MutedText style={{ fontSize: 12 }} data-testid="dedup-selection-count">
                {plural(selection.length, 'file')} selected{unusable ? ` · ${unusable} with no PDF to compare` : ''}
              </MutedText>
            </div>
          </Option>
        )}
        <Option>
          <RadioButton
            checked={mode === 'project'}
            onChange={() => setMode('project')}
            disabled={running}
            inputProps={{ 'data-testid': 'dedup-scope-project' }}
          />
          <div>
            <MedBodyText>Whole project</MedBodyText>
            <MutedText style={{ fontSize: 12 }}>{plural(candidatesIn(files, folders, null).length, 'converted PDF')} across all folders</MutedText>
          </div>
        </Option>
        <Option>
          <RadioButton
            checked={mode === 'folder'}
            onChange={() => setMode('folder')}
            disabled={running || !folders.length}
            inputProps={{ 'data-testid': 'dedup-scope-folder' }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <MedBodyText>One folder</MedBodyText>
            <div style={{ marginTop: 8, opacity: mode === 'folder' ? 1 : 0.6 }}>
              <NativeSelect
                value={folderId}
                onChange={e => {
                  setFolderId(e.target.value)
                  setMode('folder')
                }}
                disabled={running || !folders.length}
                data-testid="dedup-folder-select">
                {folders.map(g => (
                  <option key={g.id} value={g.id}>
                    {folderPath(folders, g.id)}
                  </option>
                ))}
              </NativeSelect>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, cursor: 'pointer' }}>
                <TableCheckbox
                  checked={recursive}
                  disabled={running}
                  onChange={e => {
                    setRecursive(e.target.checked)
                    setMode('folder')
                  }}
                  inputProps={{ 'data-testid': 'dedup-recursive' }}
                />
                <MedBodyText>Include subfolders ({plural(candidatesIn(files, folders, folderId, true).length, 'converted PDF')})</MedBodyText>
              </label>
            </div>
          </div>
        </Option>
      </div>
      <div style={{ marginTop: 16, minHeight: 44 }}>
        {running && (
          <div data-testid="dedup-running" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spinner size={18} tone={colors.primary} />
            <MedBodyText>
              Comparing {plural(compared, 'file')} in {scopeLabel}…
            </MedBodyText>
          </div>
        )}
        {!running && !enough && (
          <MutedText data-testid="dedup-hint">
            {chosen && unusable
              ? `${unusable} of the selected files ${unusable === 1 ? 'has' : 'have'} no PDF to compare. Convert ${unusable === 1 ? 'it' : 'them'}, or narrow the selection.`
              : 'Fewer than two converted PDFs in this scope. Convert files first.'}
          </MutedText>
        )}
        {error && (
          <ErrorText role="alert" style={{ margin: 0 }} data-testid="dedup-error">
            {error}
          </ErrorText>
        )}
      </div>
      <DialogActions style={{ marginTop: 12 }}>
        <SecondaryButton type="button" onClick={onClose} data-testid="dialog-cancel">
          Cancel
        </SecondaryButton>
        <Button type="button" onClick={start} disabled={!enough || (mode === 'folder' && !folderId)} loading={running} data-testid="dedup-start">
          <DifferenceOutlinedIcon /> {running ? 'Comparing…' : 'Find duplicates'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
